import { ApolloClient, InMemoryCache, split } from '@apollo/client'
import { loadErrorMessages, loadDevMessages } from '@apollo/client/dev'
import { BatchHttpLink } from '@apollo/client/link/batch-http'
import { setContext } from '@apollo/client/link/context'
import { GraphQLWsLink } from '@apollo/client/link/subscriptions'
import { getMainDefinition } from '@apollo/client/utilities'
import { getRealIpAddress } from '@src/lib/CustomSSR/real-ip-address'
import asyncSleep from '@src/lib/asyncSleep'
import { readHubspotUtk } from '@src/lib/hubspotUtk'
import { scoCollectionPagination } from '@src/lib/scoCollectionPagination'
import { IS_SERVER_SIDE, IS_SSR } from '@src/lib/server-side-rendering'
import { scoBackendUrl } from '@src/lib/startups-url'
import {
  getWsConnectionState,
  initialWsConnectionState,
  setWsConnectionState,
  WS_DO_NOT_RETRY_CODES,
  WS_RETRY_ATTEMPTS,
  WS_RETRY_WAIT_MS,
} from '@src/lib/wsConnectionStatus'
import { createClient } from 'graphql-ws'

export let clientSideApolloClient = null
export let wsClient: ReturnType<typeof createClient> | null = null

type GetApolloClientArgs = {
  apolloState?: {
    data?: any
  }
  headers?: any
}

function createGraphQlWsClient() {
  let retryAttempt = 0

  return createClient({
    url: process.env.NEXT_PUBLIC_SCO_GQLWS_URL || scoBackendUrl('/graphql-ws').replace('http', 'ws'),
    // Browser auth is the session cookie on the WS upgrade request (see startups3 UserAuthTokenAuth).
    // Optional connectionParams.token is for sco_uat_… API tokens only — never userId.
    connectionParams: () => {
      try {
        const token = localStorage.getItem('sco:auth:websocketToken')
        // Ignore legacy userId values; only forward real UserAuthToken secrets
        if (token && token.startsWith('sco_uat_')) {
          return { token }
        }
      } catch {
        // ignore
      }
      return {}
    },
    // Keep one connection open for the app and reconnect when dropped
    lazy: false,
    // Repair window: 3 attempts × 3s ≈ 9s
    retryAttempts: WS_RETRY_ATTEMPTS,
    retryWait: async () => {
      await asyncSleep(WS_RETRY_WAIT_MS)
    },
    shouldRetry: event => {
      const code = (event as { code?: number } | null)?.code
      return code ? !WS_DO_NOT_RETRY_CODES.has(code) : true
    },
    onNonLazyError: () => {
      retryAttempt = 0
      setWsConnectionState({
        status: 'offline',
        attempt: 0,
        maxAttempts: WS_RETRY_ATTEMPTS,
      })
    },
    on: {
      connecting: isRetry => {
        if (isRetry) {
          retryAttempt += 1
          setWsConnectionState({
            status: 'reconnecting',
            attempt: retryAttempt,
            maxAttempts: WS_RETRY_ATTEMPTS,
          })
        } else {
          retryAttempt = 0
          setWsConnectionState({
            status: 'connecting',
            attempt: 0,
            maxAttempts: WS_RETRY_ATTEMPTS,
          })
        }
      },
      connected: () => {
        retryAttempt = 0
        setWsConnectionState({
          status: 'connected',
          attempt: 0,
          maxAttempts: WS_RETRY_ATTEMPTS,
        })
      },
      closed: () => {
        // Optimistic reconnecting state while graphql-ws decides whether to retry.
        // onNonLazyError will flip to offline if the repair budget is exhausted.
        const prev = getWsConnectionState()
        if (prev.status === 'offline') {
          return
        }
        setWsConnectionState({
          status: 'reconnecting',
          attempt: Math.max(prev.attempt, 1),
          maxAttempts: WS_RETRY_ATTEMPTS,
        })
      },
    },
  })
}

export const createApolloClient = (args?: GetApolloClientArgs) => {
  if (process.env.NEXT_PUBLIC_DEPLOYMENT_ENV !== 'production') {
    loadDevMessages()
    loadErrorMessages()
  }

  const realIpAddress = getRealIpAddress(args?.headers || {})

  const headers = {
    cookie: args?.headers?.cookie,
    ...(IS_SSR ? { 'x-sco-ssr': 'true' } : {}),
    ...(realIpAddress !== '0.0.0.0' ? { 'x-startups5-user-ip': realIpAddress } : {}),
  }

  const batchHttpLink = new BatchHttpLink({
    uri: scoBackendUrl('/graphql'),
    credentials: 'include',
    headers,
    batchMax: IS_SSR ? 100 : 1,
    batchInterval: 1,
  })

  // Referer link (context link)
  const refererLink = setContext((operation, prevContext) => {
    const hutk = readHubspotUtk(IS_SERVER_SIDE ? args?.headers?.cookie : undefined)
    return {
      headers: {
        ...prevContext.headers,
        'content-type': 'application/json',
        'x-apollo-operation-name': 'true',
        'x-sco-referer': (!IS_SERVER_SIDE && window.location.href) || process.env.NEXT_PUBLIC_URL,
        ...(hutk ? { 'x-sco-hutk': hutk } : {}),
      },
      ...prevContext,
    }
  })

  // Reset status when (re)creating the client
  if (!IS_SERVER_SIDE) {
    setWsConnectionState({ ...initialWsConnectionState })
  }

  // WebSocket Link for Subscriptions
  const wsLink = !IS_SERVER_SIDE ? new GraphQLWsLink((wsClient = createGraphQlWsClient())) : null

  // Using split to direct subscriptions to wsLink and others to batchHttpLink
  const splitLink = wsLink
    ? split(
        ({ query }) => {
          const definition = getMainDefinition(query)
          return definition.kind === 'OperationDefinition' && definition.operation === 'subscription'
        },
        wsLink,
        batchHttpLink
      )
    : batchHttpLink

  // Combine with refererLink
  const link = refererLink.concat(splitLink)

  return new ApolloClient({
    link,
    ssrMode: IS_SSR,
    cache: new InMemoryCache({
      typePolicies: {
        BeancounterPaymentCollection: scoCollectionPagination(),
        BeancounterTransactionCollection: scoCollectionPagination(),
        GroupEventCollection: scoCollectionPagination(),
        GroupNotificationCollection: scoCollectionPagination(),
        GroupPostCollection: scoCollectionPagination(),
        GroupRequestCollection: scoCollectionPagination(),
        ScoCommentCollection: scoCollectionPagination(),
        ScoEmailMessageCollection: scoCollectionPagination(),
        ScoInvestorMatchCollection: scoCollectionPagination(),
        ScoInvestorProfileCollection: scoCollectionPagination(),
        ScoNotificationCollection: scoCollectionPagination(),
        ScoSalesEventCollection: scoCollectionPagination(),
        ScoThreadCollection: scoCollectionPagination(),
        ScoInboxThreadCollection: scoCollectionPagination(),
        ScoUserCollection: scoCollectionPagination(),
      },
    }).restore(args?.apolloState?.data || {}),
  })
}

export const getClientSideApolloClient = apolloState => {
  if (!clientSideApolloClient && !IS_SSR) {
    clientSideApolloClient = createApolloClient({ apolloState })
  }
  return clientSideApolloClient
}
